import { useMemo, useState } from 'react';
import {
  Plus,
  RefreshCw,
  TrendingUp,
  Coins,
  Wallet,
  AlertTriangle,
  Upload,
  Repeat,
  Eraser,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { TabsRoot, TabsList, TabsTab, TabsPanel } from '../ui/tabs';
import { Select } from '../ui/select-custom';
import { InvestmentAssetDialog } from '../dialogs/investment-asset-dialog';
import { TradeDialog } from '../dialogs/trade-dialog';
import { EarningDialog } from '../dialogs/earning-dialog';
import { ConfirmDialog } from '../dialogs/confirm-dialog';
import { ImportB3Dialog } from '../dialogs/import-b3-dialog';
import { ConversionDialog } from '../dialogs/conversion-dialog';
import {
  PortfolioTable,
  TradesTable,
  EarningsTable,
  EmptyState,
} from './investment-tables';
import {
  AllocationByType,
  AllocationByAsset,
  EarningsByMonth,
  ContributionHistory,
  ChartCard,
} from './portfolio-charts';
import { IrpfTables } from './irpf-tables';
import { useAppData } from '../../contexts/app-data-context';
import { useRoute } from '../../hooks/use-route';
import type { InvestmentsTabId } from '../../lib/routes';
import { formatCurrency } from '../../lib/format';
import { quotesConfigured } from '../../lib/quotes';
import {
  computeRealizedSales,
  totalEarningsReceived,
  yieldOnCost,
} from '../../lib/investments';
import { BITCOIN_COLOR, earningKinds, investmentTypes } from '../../types';
import type { Earning, InvestmentAsset, Trade } from '../../types';
import type { InvestmentAssetFormData } from '../forms/investment-asset-form';
import type { TradeFormData } from '../forms/trade-form';
import type { EarningFormData } from '../forms/earning-form';

interface StatTileProps {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'positive' | 'negative';
  icon?: React.ReactNode;
}

function StatTile({ label, value, hint, tone = 'neutral', icon }: StatTileProps) {
  const valueTone =
    tone === 'positive'
      ? 'text-green-600'
      : tone === 'negative'
        ? 'text-red-600'
        : 'text-foreground';
  return (
    <Card className="gap-2 p-4">
      <div className="flex items-center gap-2">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span className="text-xs font-medium text-foreground-subtle">{label}</span>
      </div>
      <span className={`text-xl font-semibold ${valueTone}`}>{value}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </Card>
  );
}

export function InvestmentsSection() {
  const {
    investmentAssets,
    activeInvestmentAssets,
    trades,
    earnings,
    corporateActions,
    loadingInvestments,
    positions,
    portfolioSummary,
    addInvestmentAsset,
    updateInvestmentAsset,
    deleteInvestmentAsset,
    addTrade,
    updateTrade,
    deleteTrade,
    addEarning,
    updateEarning,
    deleteEarning,
    refreshQuotes,
    refreshingQuotes,
    quotesError,
    importInvestments,
    conversions,
    addConversion,
    orphanRecords,
    cleanupOrphanRecords,
    mismatchedEarnings,
    fixEarningKinds,
    caixinhasTotal,
    bitcoinTotal,
  } = useAppData();

  const { investmentsTab, goToInvestmentsTab } = useRoute();

  const portfolio = useMemo(
    () => ({ assets: investmentAssets, trades, earnings, corporateActions, conversions }),
    [investmentAssets, trades, earnings, corporateActions, conversions]
  );

  // Dialogs
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<InvestmentAsset | null>(null);
  const [assetToDelete, setAssetToDelete] = useState<InvestmentAsset | null>(null);

  const [tradeDialogOpen, setTradeDialogOpen] = useState(false);
  const [editingTrade, setEditingTrade] = useState<Trade | null>(null);
  const [tradeToDelete, setTradeToDelete] = useState<Trade | null>(null);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const [fixingKinds, setFixingKinds] = useState(false);
  const orphanCount =
    orphanRecords.trades.length + orphanRecords.earnings.length;

  const [earningDialogOpen, setEarningDialogOpen] = useState(false);
  const [editingEarning, setEditingEarning] = useState<Earning | null>(null);
  const [earningToDelete, setEarningToDelete] = useState<Earning | null>(null);

  // Ano de referência dos indicadores de proventos (o IRPF tem o seu próprio).
  const currentYear = new Date().getFullYear();
  const [statsYear, setStatsYear] = useState(currentYear);

  const earningYears = useMemo(() => {
    const years = new Set<number>([currentYear]);
    for (const earning of earnings) years.add(earning.paymentDate.getFullYear());
    return [...years].sort((a, b) => b - a);
  }, [earnings, currentYear]);

  /**
   * Alocação do patrimônio inteiro, não só da corretora: as caixinhas são
   * renda fixa e entram junto com o Tesouro; o bitcoin fica à parte.
   */
  const allocationSlices = useMemo(
    () => [
      {
        key: 'acao',
        label: investmentTypes.acao.label,
        color: investmentTypes.acao.color,
        value: portfolioSummary.byType.acao.marketValue,
      },
      {
        key: 'fii',
        label: investmentTypes.fii.label,
        color: investmentTypes.fii.color,
        value: portfolioSummary.byType.fii.marketValue,
      },
      {
        key: 'renda-fixa',
        label: 'Renda fixa',
        color: investmentTypes.tesouro.color,
        value: portfolioSummary.byType.tesouro.marketValue + caixinhasTotal,
      },
      { key: 'btc', label: 'Bitcoin', color: BITCOIN_COLOR, value: bitcoinTotal },
    ],
    [portfolioSummary, caixinhasTotal, bitcoinTotal]
  );

  const realizedSales = useMemo(() => computeRealizedSales(portfolio), [portfolio]);

  const resultByTradeId = useMemo(
    () => new Map(realizedSales.map((sale) => [sale.tradeId, sale.result])),
    [realizedSales]
  );

  /** Proventos líquidos por ativo (exclui amortização, que não é rendimento). */
  const earningsByAsset = useMemo(() => {
    const totals = new Map<string, number>();
    for (const earning of earnings) {
      const treatment = earningKinds[earning.kind]?.treatment;
      if (treatment !== 'isento' && treatment !== 'exclusiva') continue;
      const net = earning.grossValue - earning.irrfWithheld;
      totals.set(earning.assetId, (totals.get(earning.assetId) ?? 0) + net);
    }
    return totals;
  }, [earnings]);

  const earningsInYear = useMemo(
    () => totalEarningsReceived(earnings, statsYear),
    [earnings, statsYear]
  );

  const monthlyEarnings = useMemo(() => {
    const months = Array(12).fill(0) as number[];
    for (const earning of earnings) {
      if (earning.paymentDate.getFullYear() !== statsYear) continue;
      const treatment = earningKinds[earning.kind]?.treatment;
      if (treatment !== 'isento' && treatment !== 'exclusiva') continue;
      months[earning.paymentDate.getMonth()]! += earning.grossValue - earning.irrfWithheld;
    }
    return months;
  }, [earnings, statsYear]);

  const realizedInYear = useMemo(
    () =>
      realizedSales
        .filter((sale) => sale.date.getFullYear() === statsYear)
        .reduce((sum, sale) => sum + sale.result, 0),
    [realizedSales, statsYear]
  );

  const hasAssets = activeInvestmentAssets.length > 0;

  // ---------- handlers ----------

  const handleAssetSubmit = async (data: InvestmentAssetFormData) => {
    const payload = {
      ticker: data.ticker,
      type: data.type,
      name: data.name,
      cnpj: data.cnpj,
      currentPrice: data.currentPrice > 0 ? data.currentPrice : undefined,
      priceUpdatedAt: data.currentPrice > 0 ? new Date() : undefined,
    };
    if (editingAsset) {
      await updateInvestmentAsset(editingAsset.id, payload);
    } else {
      await addInvestmentAsset(payload);
    }
    setAssetDialogOpen(false);
    setEditingAsset(null);
  };

  const handleTradeSubmit = async (data: TradeFormData) => {
    if (editingTrade) {
      await updateTrade(editingTrade.id, data);
      setTradeDialogOpen(false);
      setEditingTrade(null);
    } else {
      await addTrade(data);
    }
  };

  const handleEarningSubmit = async (data: EarningFormData) => {
    if (editingEarning) {
      await updateEarning(editingEarning.id, data);
      setEarningDialogOpen(false);
      setEditingEarning(null);
    } else {
      await addEarning(data);
    }
  };

  const openNewAsset = () => {
    setEditingAsset(null);
    setAssetDialogOpen(true);
  };

  const openNewTrade = () => {
    setEditingTrade(null);
    setTradeDialogOpen(true);
  };

  const openNewEarning = () => {
    setEditingEarning(null);
    setEarningDialogOpen(true);
  };

  if (loadingInvestments) {
    return (
      <Card>
        <EmptyState>Carregando investimentos...</EmptyState>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Ações */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={openNewAsset}>
          <Plus className="size-3.5" />
          Papel
        </Button>
        <Button size="sm" variant="outline" onClick={openNewTrade} disabled={!hasAssets}>
          <Plus className="size-3.5" />
          Operação
        </Button>
        <Button size="sm" variant="outline" onClick={openNewEarning} disabled={!hasAssets}>
          <Plus className="size-3.5" />
          Provento
        </Button>
        <Button size="sm" variant="outline" onClick={() => setImportDialogOpen(true)}>
          <Upload className="size-3.5" />
          Importar B3
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setConversionDialogOpen(true)}
          disabled={activeInvestmentAssets.length < 2}
          title="Incorporação de fundo ou troca de ticker"
        >
          <Repeat className="size-3.5" />
          Conversão
        </Button>
        {quotesConfigured && (
          <Button
            size="sm"
            variant="ghost"
            onClick={refreshQuotes}
            disabled={refreshingQuotes || !hasAssets}
            title="Buscar cotações na brapi"
          >
            <RefreshCw className={`size-3.5 ${refreshingQuotes ? 'animate-spin' : ''}`} />
            {refreshingQuotes ? 'Atualizando...' : 'Cotações'}
          </Button>
        )}
      </div>

      {orphanCount > 0 && (
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">
                {orphanCount} {orphanCount === 1 ? 'registro ficou' : 'registros ficaram'} sem
                papel.
              </span>
              <span>
                {orphanRecords.trades.length > 0 &&
                  `${orphanRecords.trades.length} ${orphanRecords.trades.length === 1 ? 'operação' : 'operações'}`}
                {orphanRecords.trades.length > 0 && orphanRecords.earnings.length > 0 && ' e '}
                {orphanRecords.earnings.length > 0 &&
                  `${orphanRecords.earnings.length} ${orphanRecords.earnings.length === 1 ? 'provento' : 'proventos'}`}{' '}
                de papéis excluídos. Aparecem com “—” no lugar do ticker e atrapalham a
                deduplicação da importação.
              </span>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={cleaningOrphans}
            onClick={async () => {
              setCleaningOrphans(true);
              try {
                await cleanupOrphanRecords();
              } finally {
                setCleaningOrphans(false);
              }
            }}
          >
            <Eraser className="size-3.5" />
            {cleaningOrphans ? 'Removendo...' : 'Remover'}
          </Button>
        </div>
      )}

      {mismatchedEarnings.length > 0 && (
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <div className="flex flex-col gap-0.5">
              <span className="font-medium">
                {mismatchedEarnings.length}{' '}
                {mismatchedEarnings.length === 1
                  ? 'provento está com o tipo trocado'
                  : 'proventos estão com o tipo trocado'}
                .
              </span>
              <span>
                {[...new Set(
                  mismatchedEarnings.map(
                    (e) =>
                      investmentAssets.find((a) => a.id === e.assetId)?.ticker ?? '?'
                  )
                )].join(', ')}
                : dividendo em FII ou rendimento de FII em ação. O valor não muda — é
                a mesma isenção — mas o IRPF usa fichas diferentes para cada um.
              </span>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={fixingKinds}
            onClick={async () => {
              setFixingKinds(true);
              try {
                await fixEarningKinds();
              } finally {
                setFixingKinds(false);
              }
            }}
          >
            <Repeat className="size-3.5" />
            {fixingKinds ? 'Corrigindo...' : 'Corrigir'}
          </Button>
        </div>
      )}

      {quotesError && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>{quotesError}</span>
        </div>
      )}

      {!hasAssets ? (
        <Card>
          <EmptyState>
            Cadastre seu primeiro papel para começar a acompanhar a carteira.
          </EmptyState>
        </Card>
      ) : (
        <>
          {/* Resumo */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              label="Patrimônio atual"
              value={`R$ ${formatCurrency(portfolioSummary.marketValue)}`}
              hint={
                portfolioSummary.partialPricing
                  ? 'Papéis sem cotação entram pelo custo'
                  : undefined
              }
              icon={<Wallet className="size-4" />}
            />
            <StatTile
              label="Total investido"
              value={`R$ ${formatCurrency(portfolioSummary.totalCost)}`}
              hint="Custo de aquisição, com taxas"
            />
            <StatTile
              label="Lucro/prejuízo não realizado"
              value={`${portfolioSummary.unrealizedGain >= 0 ? '+' : '-'}R$ ${formatCurrency(
                Math.abs(portfolioSummary.unrealizedGain)
              )}`}
              hint={`${portfolioSummary.unrealizedGainPct >= 0 ? '+' : ''}${portfolioSummary.unrealizedGainPct.toFixed(2)}%`}
              tone={portfolioSummary.unrealizedGain >= 0 ? 'positive' : 'negative'}
              icon={<TrendingUp className="size-4" />}
            />
            <StatTile
              label={`Proventos em ${statsYear}`}
              value={`R$ ${formatCurrency(earningsInYear)}`}
              hint={`Yield on cost ${yieldOnCost(earningsInYear, portfolioSummary.totalCost).toFixed(2)}%`}
              tone={earningsInYear > 0 ? 'positive' : 'neutral'}
              icon={<Coins className="size-4" />}
            />
          </div>

          <TabsRoot
            value={investmentsTab}
            onValueChange={(value) => goToInvestmentsTab(value as InvestmentsTabId)}
          >
            <TabsList>
              <TabsTab value="carteira">Carteira</TabsTab>
              <TabsTab value="operacoes">Operações</TabsTab>
              <TabsTab value="proventos">Proventos</TabsTab>
              <TabsTab value="irpf">IRPF</TabsTab>
            </TabsList>

            <TabsPanel value="carteira">
              <div className="flex flex-col gap-4">
                <Card>
                  <PortfolioTable
                    positions={positions}
                    earningsByAsset={earningsByAsset}
                    totalMarketValue={portfolioSummary.marketValue}
                    onEditAsset={(asset) => {
                      setEditingAsset(asset);
                      setAssetDialogOpen(true);
                    }}
                    onDeleteAsset={setAssetToDelete}
                  />
                </Card>

                <div className="grid gap-4 lg:grid-cols-2">
                  {positions.length > 0 && (
                    <ChartCard
                      title="Alocação por tipo"
                      description="Patrimônio inteiro: carteira, caixinhas e bitcoin. Renda fixa soma Tesouro e caixinhas."
                    >
                      <AllocationByType slices={allocationSlices} />
                    </ChartCard>
                  )}
                  {positions.length > 1 && (
                    <ChartCard title="Alocação por papel">
                      <AllocationByAsset positions={positions} />
                    </ChartCard>
                  )}
                  {trades.length > 1 && (
                    <ChartCard
                      title="Aporte acumulado"
                      description="Custo investido ao longo do tempo (não patrimônio histórico: a API só devolve a cotação de hoje)."
                    >
                      <ContributionHistory trades={trades} />
                    </ChartCard>
                  )}
                  {monthlyEarnings.some((v) => v > 0) && (
                    <ChartCard title={`Proventos por mês em ${statsYear}`}>
                      <EarningsByMonth monthlyTotals={monthlyEarnings} year={statsYear} />
                    </ChartCard>
                  )}
                </div>
              </div>
            </TabsPanel>

            <TabsPanel value="operacoes">
              <Card className="gap-4">
                {realizedInYear !== 0 && (
                  <div className="flex items-center justify-between rounded-lg bg-muted px-3 py-2 text-sm">
                    <span className="text-muted-foreground">
                      Resultado realizado em {statsYear}
                    </span>
                    <span
                      className={`font-semibold ${
                        realizedInYear >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {realizedInYear >= 0 ? '+' : '-'}R${' '}
                      {formatCurrency(Math.abs(realizedInYear))}
                    </span>
                  </div>
                )}
                <TradesTable
                  trades={trades}
                  assets={investmentAssets}
                  resultByTradeId={resultByTradeId}
                  onEdit={(trade) => {
                    setEditingTrade(trade);
                    setTradeDialogOpen(true);
                  }}
                  onDelete={setTradeToDelete}
                />
              </Card>
            </TabsPanel>

            <TabsPanel value="proventos">
              <Card className="gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-subtle">Indicadores de</span>
                  <Select
                    value={String(statsYear)}
                    onChange={(value) => setStatsYear(Number(value))}
                    options={earningYears.map((y) => ({ value: String(y), label: String(y) }))}
                  />
                </div>
                <EarningsTable
                  earnings={earnings}
                  assets={investmentAssets}
                  onEdit={(earning) => {
                    setEditingEarning(earning);
                    setEarningDialogOpen(true);
                  }}
                  onDelete={setEarningToDelete}
                />
              </Card>
            </TabsPanel>

            <TabsPanel value="irpf">
              <IrpfTables portfolio={portfolio} />
            </TabsPanel>
          </TabsRoot>
        </>
      )}

      {/* Dialogs */}
      <ConversionDialog
        open={conversionDialogOpen}
        assets={activeInvestmentAssets}
        portfolio={portfolio}
        onSubmit={async (data) => {
          await addConversion(data);
          setConversionDialogOpen(false);
        }}
        onClose={() => setConversionDialogOpen(false)}
      />

      <ImportB3Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        assets={investmentAssets}
        trades={trades}
        earnings={earnings}
        onImport={importInvestments}
      />

      <InvestmentAssetDialog
        open={assetDialogOpen}
        editing={editingAsset}
        existingAssets={investmentAssets}
        onSubmit={handleAssetSubmit}
        onClose={() => {
          setAssetDialogOpen(false);
          setEditingAsset(null);
        }}
      />

      <TradeDialog
        open={tradeDialogOpen}
        editing={editingTrade}
        assets={activeInvestmentAssets}
        portfolio={portfolio}
        onSubmit={handleTradeSubmit}
        onClose={() => {
          setTradeDialogOpen(false);
          setEditingTrade(null);
        }}
      />

      <EarningDialog
        open={earningDialogOpen}
        editing={editingEarning}
        assets={activeInvestmentAssets}
        portfolio={portfolio}
        onSubmit={handleEarningSubmit}
        onClose={() => {
          setEarningDialogOpen(false);
          setEditingEarning(null);
        }}
      />

      <ConfirmDialog
        open={!!assetToDelete}
        message={
          <>
            Excluir <strong className="text-foreground">{assetToDelete?.ticker}</strong>? As
            operações, proventos e conversões desse papel são excluídos junto, e o histórico
            dele some do IRPF. Para só tirar da carteira sem perder o histórico, zere a
            posição com uma venda.
          </>
        }
        onConfirm={async () => {
          if (assetToDelete) await deleteInvestmentAsset(assetToDelete.id);
          setAssetToDelete(null);
        }}
        onCancel={() => setAssetToDelete(null)}
      />

      <ConfirmDialog
        open={!!tradeToDelete}
        message={
          <>
            Excluir a {tradeToDelete?.operation === 'buy' ? 'compra' : 'venda'} de{' '}
            <strong className="text-foreground">{tradeToDelete?.quantity}</strong> cotas em{' '}
            {tradeToDelete?.date.toLocaleDateString('pt-BR')}? Isso recalcula o custo médio e
            a apuração dos meses seguintes.
          </>
        }
        onConfirm={async () => {
          if (tradeToDelete) await deleteTrade(tradeToDelete.id);
          setTradeToDelete(null);
        }}
        onCancel={() => setTradeToDelete(null)}
      />

      <ConfirmDialog
        open={!!earningToDelete}
        message={
          <>
            Excluir o provento de{' '}
            <strong className="text-foreground">
              R$ {formatCurrency(earningToDelete?.grossValue ?? 0)}
            </strong>{' '}
            em {earningToDelete?.paymentDate.toLocaleDateString('pt-BR')}?
          </>
        }
        onConfirm={async () => {
          if (earningToDelete) await deleteEarning(earningToDelete.id);
          setEarningToDelete(null);
        }}
        onCancel={() => setEarningToDelete(null)}
      />
    </div>
  );
}
